import React from 'react';
import FavoriteToggle from './FavoriteToggle';

interface ExpertCardProps {
  slug: string;
  displayName: string;
  isFavorite: boolean;
  subtitle?: string;
  onClick: (slug: string) => void;
  onFavoriteToggle: (isFavorite: boolean) => void;
}

const ExpertCard: React.FC<ExpertCardProps> = ({
  slug,
  displayName,
  isFavorite,
  subtitle,
  onClick,
  onFavoriteToggle,
}) => {
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleCardClick = () => {
    onClick(slug);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when toggling favorite
  };

  return (
    <button
      onClick={handleCardClick}
      className="w-full bg-white rounded-xl border border-gray-100 p-3.5 hover:border-purple-200 hover:shadow-md transition-all duration-200 active:scale-[0.98] text-left group cursor-pointer"
    >
      <div className="flex items-center gap-3">
        {/* Expert Avatar */}
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-sm">
            {getInitials(displayName)}
          </div>
        </div>

        {/* Expert Details */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 truncate">
            {displayName}
          </h4>
          {subtitle && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {subtitle}
            </p>
          )}
        </div>

        {/* Favorite Toggle */}
        <div className="flex-shrink-0" onClick={handleFavoriteClick}>
          <FavoriteToggle
            expertSlug={slug}
            isFavorite={isFavorite}
            onToggle={onFavoriteToggle}
          />
        </div>

        {/* Arrow Icon */}
        <div className="flex-shrink-0 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </button>
  );
};

export default ExpertCard;
